import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { CreateAssessmentForm } from '@/features/assessments/forms/create-assessment-form'
import { UserManagement } from '@/features/organizations/components/user-management'
import { TrendsComparison } from '@/features/results/components/trends-comparison'
import { resultsApi } from '@/features/results/api'
import { assessmentsApi } from '@/features/assessments/api'
import { AssessmentCard } from '@/features/assessments/components/assessment-card'
import { useIndustry } from '@/features/industries/hooks'
import { MaturityLevelBar } from '@/features/organizations/components/maturity-level-bar'
import { useMaturityColorMap } from '@/features/maturity-levels/maturity-color-utils'
import {
  Building2, FileText, ArrowLeft, ArrowRight, Plus, Eye, Download, ClipboardList
} from 'lucide-react'
import { api } from '@/lib/api-client'
import { extractItems } from '@/lib/response-utils'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { useToast } from '@/hooks/use-toast'
import { useAttachments, useServeFile } from '@/features/assessments/hooks'
import { FilePreviewDialog } from '@/features/assessments/components/file-preview-dialog'

export function OrganizationDetailPage() {
  const { id } = useParams()
  const [showCreateAssessmentForm, setShowCreateAssessmentForm] = useState(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  
  const [previewOpen, setPreviewOpen] = useState(false)
  const [selectedAttachment, setSelectedAttachment] = useState<any>(null)

  const { data: organization, isLoading, error } = useQuery({
    queryKey: ['organization', id],
    queryFn: () => api.organizations.get(id!),
    enabled: !!id,
    retry: false // Don't retry on 404 errors
  })

  const { data: assessments } = useQuery({
    queryKey: ['assessments', 'organization', id],
    queryFn: () => api.assessments.list().then(res =>
      extractItems(res).filter((a: any) => a.organization_id === id!)
    ),
    enabled: !!id
  })
  const { data: latestAttachmentsRes, isLoading: isAttachmentsLoading } = useAttachments({ 
    org_id: id, 
    per_page: 3 
  })

  const { mutate: downloadFile } = useServeFile()

  // Delete assessment mutation
  const deleteAssessmentMutation = useMutation({
    mutationFn: (assessmentId: string) => assessmentsApi.delete(assessmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] })
      toast({ title: 'Assessment deleted', description: 'The assessment and its answers have been removed.' })
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to delete',
        description: error.response?.data?.title || 'Could not delete the assessment.',
        variant: 'destructive',
      })
    },
  })

  // Start assessment mutation (draft → in_progress via backend)
  const startAssessmentMutation = useMutation({
    mutationFn: (assessmentId: string) => assessmentsApi.start(assessmentId),
    onSuccess: (_data, assessmentId) => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] })
      navigate(`/assessments/${assessmentId}/questionnaire`)
    },
    onError: (error: any) => {
      toast({
        title: 'Cannot Start Assessment',
        description: error.response?.data?.title || 'Failed to start assessment.',
        variant: 'destructive',
      })
    },
  })

  // Extract org data early so hooks below can reference it
  const org = organization?.data

  // Fetch results for each completed assessment to build trend data
  const completedAssessments = assessments?.filter((a: any) => a.status === 'completed') ?? []
  const resultsQueries = useQueries({
    queries: completedAssessments.map((a: any) => ({
      queryKey: ['results', a.id],
      queryFn: () => resultsApi.get(a.id).then(res => ({ ...res.data, _assessment: a })),
      enabled: !!a.id,
    })),
  })

  // Fetch industry detail (with benchmarks) — must be called before any early return
  const { data: industryDetail } = useIndustry(org?.industry_id || '')
  const { map: colorMap } = useMaturityColorMap()

  // Build trend data from all completed assessment results
  const trendData = useMemo(() => {
    const points = resultsQueries
      .filter(q => q.data && q.data.domain_scores)
      .map(q => {
        const r = q.data
        const date = r._assessment?.completed_at || r._assessment?.created_at || ''
        const overallScore = r.overall_score?.maturity_level ?? Math.round((r.overall_score?.domain_score ?? 0) * 5)
        const point: { date: string; overall: number;[key: string]: string | number | undefined } = {
          date: date ? new Date(date).toLocaleDateString() : 'N/A',
          overall: overallScore,
        }
        // Add per-domain maturity levels
        if (r.domain_scores) {
          Object.entries(r.domain_scores).forEach(([_key, s]: [string, any]) => {
            const domainName = (s.domain ?? _key).replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
            point[domainName.toLowerCase().replace(/\s+/g, '_')] = s.maturity_level ?? Math.round((s.domain_score ?? 0) * 5)
          })
        }
        return { point, sortDate: new Date(date || 0).getTime() }
      })
      .sort((a, b) => a.sortDate - b.sortDate)
      .map(p => p.point)
    return points
  }, [resultsQueries])

  // Build benchmark data for TrendsComparison
  const benchmarkData = useMemo(() => {
    if (!industryDetail?.benchmarks || industryDetail.benchmarks.length === 0) return null
    return {
      industry_name: industryDetail.name,
      benchmarks: industryDetail.benchmarks.map(b => ({
        domain_name: b.domain_name || '',
        target_level: b.target_level,
        description: b.description,
      })),
    }
  }, [industryDetail])

  // Get latest domain scores from the most recent completed assessment
  const latestDomainScores = useMemo(() => {
    const latestResult = resultsQueries
      .filter(q => q.data?.domain_scores)
      .sort((a, b) => {
        const dateA = new Date(a.data?._assessment?.completed_at || 0).getTime()
        const dateB = new Date(b.data?._assessment?.completed_at || 0).getTime()
        return dateB - dateA
      })[0]
    return latestResult?.data?.domain_scores ?? undefined
  }, [resultsQueries])



  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  // Handle error case (organization not found)
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Organization Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            The organization you're looking for doesn't exist or you don't have access to it.
          </p>
          <div className="space-x-4">
            <Button onClick={() => window.history.back()}>
              Go Back
            </Button>
            <Link to="/dashboard">
              <Button variant="outline">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="md:flex md:items-end md:justify-between gap-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center space-x-4 mb-4">
            <Link to="/organizations">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Organizations
              </Button>
            </Link>
          </div>
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            {org?.name || 'Organization Details'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {org?.industry_name} • {org?.size} • Created {new Date(org?.created_at || '').toLocaleDateString()}
          </p>
        </div>

        {/* Maturity Level Bar — right-aligned */}
        {(() => {
          const latestResult = resultsQueries
            .filter(q => q.data?.overall_score)
            .sort((a, b) => {
              const dateA = new Date(a.data?._assessment?.completed_at || 0).getTime()
              const dateB = new Date(b.data?._assessment?.completed_at || 0).getTime()
              return dateB - dateA
            })[0]?.data

          const level = latestResult?.overall_score?.maturity_level ?? null
          const levelName = latestResult?.overall_score?.maturity_level_name ?? null
          const benchmarks = industryDetail?.benchmarks
          const standard = benchmarks && benchmarks.length > 0
            ? benchmarks.reduce((sum: number, b: any) => sum + b.target_level, 0) / benchmarks.length
            : null

          if (level == null) return null

          return (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
            >
              <Card className="w-96 shrink-0">
                <CardContent className="py-4 px-5">
                  <MaturityLevelBar
                    level={level}
                    levelName={levelName}
                    color={level != null ? (colorMap.get(level)?.color ?? null) : null}
                    industryStandard={standard}
                    barHeight="h-2.5"
                  />
                </CardContent>
              </Card>
            </motion.div>
          )
        })()}
      </div>

      {/* Organization Info */}
      <motion.div
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="h-5 w-5" />
                <span>Organization Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900">Description</h4>
                <p className="text-gray-600 mt-1">
                  {org?.description || 'No description provided.'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900">Industry</h4>
                  <p className="text-gray-600">{org?.industry_name || 'Not specified'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Size</h4>
                  <p className="text-gray-600">{org?.size || 'Not specified'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <AuthorizationGate actions={['view_assessment']}>
          <div>
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-5 w-5" />
                  <span>Quick Stats</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {assessments?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total Assessments</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {assessments?.filter((a: any) => a.status === 'completed').length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">
                    {assessments?.filter((a: any) => a.status === 'in_progress').length || 0}
                  </div>
                  <div className="text-sm text-gray-600">In Progress</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </AuthorizationGate>
      </motion.div>

      {/* Assessments */}
      <AuthorizationGate actions={['view_assessment']}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Assessments</CardTitle>
                <CardDescription>
                  IT maturity assessments for this organization
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/assessments?organization_id=${id}`}>
                    View All
                    <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Link>
                </Button>
                <AuthorizationGate actions={['create_assessment']}>
                  <Button size="sm" onClick={() => setShowCreateAssessmentForm(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    New Assessment
                  </Button>
                </AuthorizationGate>
              </div>
            </CardHeader>
            <CardContent>
              {assessments && assessments.length > 0 ? (
                <div className="space-y-3">
                  {[...assessments]
                    .sort((a: any, b: any) => {
                      // Priority: in_progress > draft > completed/cancelled
                      const statusOrder: Record<string, number> = { in_progress: 0, draft: 1, completed: 2, cancelled: 3 }
                      const sa = statusOrder[a.status] ?? 4
                      const sb = statusOrder[b.status] ?? 4
                      if (sa !== sb) return sa - sb
                      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    })
                    .slice(0, 3)
                    .map((assessment: any, i: number) => (
                      <AssessmentCard
                        key={assessment.id}
                        assessment={assessment}
                        index={i}
                        onStart={(a) => startAssessmentMutation.mutate(a.id)}
                        onDelete={(a) => {
                          if (window.confirm(`Delete "${a.name}" and all its answers?`)) {
                            deleteAssessmentMutation.mutate(a.id)
                          }
                        }}
                      />
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Assessments Yet
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Start by creating your first IT maturity assessment.
                  </p>
                  <AuthorizationGate actions={['create_assessment']}>
                    <Button onClick={() => setShowCreateAssessmentForm(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Assessment
                    </Button>
                  </AuthorizationGate>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AuthorizationGate>

      {/* Trends & Benchmarks */}
      <AuthorizationGate actions={['view_assessment']}>
        <TrendsComparison
          trendData={trendData}
          benchmarkData={benchmarkData}
          assessmentName={org?.name || 'Organization'}
          domainScores={latestDomainScores}
        />
      </AuthorizationGate>

      {/* Recent Attachments */}
      <AuthorizationGate actions={['view_assessment']}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Recent Attachments</CardTitle>
                <CardDescription>Latest evidence files uploaded for this organization</CardDescription>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/assessments/attachments?org_id=${id}`}>
                  View All
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {isAttachmentsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-50 animate-pulse rounded-lg" />)}
                </div>
              ) : latestAttachmentsRes?.items && latestAttachmentsRes.items.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {latestAttachmentsRes.items.map((att: any) => (
                    <div key={att.id} className="flex flex-col p-3 border rounded-lg hover:border-blue-200 hover:bg-blue-50/30 transition-all group">
                      <div className="flex items-start justify-between mb-2">
                        <div className="p-2 bg-blue-50 rounded text-blue-600">
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              setSelectedAttachment(att)
                              setPreviewOpen(true)
                            }}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => downloadFile({ 
                              assessmentId: att.assessment_id, 
                              questionId: att.question_id 
                            })}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm font-medium truncate mb-1" title={att.filename}>
                        {att.filename}
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-gray-400">
                        <ClipboardList className="h-2.5 w-2.5" />
                        <span className="truncate">{att.assessment_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 text-sm">
                  No attachments found for this organization.
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AuthorizationGate>

      {/* User Management */}
      <AuthorizationGate actions={['view_members']}>
        <UserManagement organizationId={id!} />
      </AuthorizationGate>

      {/* Create Assessment Form */}
      {
        showCreateAssessmentForm && (
          <CreateAssessmentForm
            organizationId={id!}
            onClose={() => setShowCreateAssessmentForm(false)}
            onSuccess={() => {
              // Refresh assessments data
              setShowCreateAssessmentForm(false)
            }}
          />
        )
      }
      {/* Preview Dialog */}
      {selectedAttachment && (
        <FilePreviewDialog 
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          assessmentId={selectedAttachment.assessment_id}
          questionId={selectedAttachment.question_id}
          filename={selectedAttachment.filename}
        />
      )}
    </motion.div>
  )
}
